import { type UIEvent, useRef, useState } from 'react';
import { Boton } from '../components/ui/Boton.js';
import { Cargando, ErrorDeCarga } from '../components/ui/Estado.js';
import { CircleCheck, ICONO } from '../components/ui/iconos.js';
import { PACIENTE } from '../copy/index.js';
import type { TextoDeConsentimiento } from './api.js';
import { Stepper, TituloDelPaso } from './Stepper.js';

type PasoConsentimientoProps = {
  texto: TextoDeConsentimiento | undefined;
  cargando: boolean;
  errorDeCarga: boolean;
  onReintentar: () => void;
  onAceptar: (version: string, hash: string) => void;
  enviando: boolean;
  error?: string | undefined;
};

/**
 * P-02b · consentimiento informado.
 *
 * **El scroll completo es obligatorio.** No es fricción por fricción: un
 * consentimiento que se puede aceptar sin haber llegado al final no prueba nada,
 * y lo que este paso produce es justamente una prueba — versión, hash y fecha
 * de lo que la persona leyó.
 *
 * El botón permanece visible y deshabilitado en vez de aparecer al final: que
 * salga de la nada cuando terminás de leer es peor que verlo esperándote.
 *
 * E3.8.2, contra la referencia P-02b: el texto legal no se dibuja, llega del
 * servidor; acá se diseña el marco. Al llegar al final la confirmación aparece
 * en teal, con tilde y con palabras, y la versión queda a la vista.
 */
export function PasoConsentimiento({
  texto,
  cargando,
  errorDeCarga,
  onReintentar,
  onAceptar,
  enviando,
  error,
}: PasoConsentimientoProps) {
  const [leidoCompleto, setLeidoCompleto] = useState(false);
  const contenedor = useRef<HTMLElement>(null);

  function alDesplazar(evento: UIEvent<HTMLElement>) {
    const { scrollTop, scrollHeight, clientHeight } = evento.currentTarget;
    // 16px de margen: en móvil el scroll rara vez llega al píxel exacto y exigir
    // el final absoluto deja el botón trabado sin explicación.
    if (scrollTop + clientHeight >= scrollHeight - 16) setLeidoCompleto(true);
  }

  return (
    <section className="flex flex-col">
      <Stepper paso={2} />

      <div className="flex flex-col gap-5 px-5 py-6">
        <div>
          <TituloDelPaso>{PACIENTE.onboarding.consentimiento.titulo}</TituloDelPaso>
          <p className="m-0 mt-1 text-lg text-body">
            {PACIENTE.onboarding.consentimiento.instruccion}
          </p>
        </div>

        {cargando ? <Cargando mensaje={PACIENTE.onboarding.consentimiento.cargando} /> : null}

        {errorDeCarga ? (
          <ErrorDeCarga
            detalle={PACIENTE.onboarding.consentimiento.errorDetalle}
            onReintentar={onReintentar}
            titulo={PACIENTE.onboarding.consentimiento.errorTitulo}
          />
        ) : null}

        {texto ? (
          <>
            {/*
              Región desplazable enfocable: WCAG 2.1.1 exige que todo contenido con
              scroll propio se pueda recorrer con el teclado, y sin tabIndex el
              contenedor no recibe foco. El <section> con aria-label es lo que la
              hace anunciable como región.
            */}
            <section
              aria-label={PACIENTE.onboarding.consentimiento.regionParaLector}
              className="max-h-75 overflow-y-auto rounded-lg bg-card p-5 text-md leading-relax whitespace-pre-line text-foreground outline-2 -outline-offset-2 outline-border"
              onScroll={alDesplazar}
              ref={contenedor}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: región desplazable, WCAG 2.1.1
              tabIndex={0}
            >
              {texto.texto}
            </section>

            <p
              aria-live="polite"
              className={`m-0 flex items-center gap-2 text-lg ${
                leidoCompleto ? 'font-bold text-accent-strong' : 'text-subtle'
              }`}
            >
              {leidoCompleto ? (
                <CircleCheck aria-hidden="true" className={`${ICONO.normal} shrink-0`} />
              ) : null}
              {leidoCompleto
                ? PACIENTE.onboarding.consentimiento.leido
                : PACIENTE.onboarding.consentimiento.faltaLeer}
            </p>

            <p className="m-0 text-base text-subtle">
              {PACIENTE.onboarding.consentimiento.version(texto.version)}
            </p>

            {error ? (
              <p className="m-0 text-md font-bold text-destructive-fg" role="alert">
                {error}
              </p>
            ) : null}

            <Boton
              cargando={enviando}
              disabled={!leidoCompleto}
              onClick={() => onAceptar(texto.version, texto.hash)}
            >
              {PACIENTE.onboarding.consentimiento.aceptar}
            </Boton>
          </>
        ) : null}
      </div>
    </section>
  );
}
